import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["trigger", "entryPoint", "entryPointParams", "entryPointProps", "fallback", "errorFallback", "onError", "onClose"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useMemo, useState } from 'react';
import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';
import { Popup } from '@atlaskit/popup';
import Spinner from '@atlaskit/spinner';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import { InternalEntryPointContainer } from '@atlassian/internal-entry-point-container';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { useClickToCloseRef } from './useClickToCloseRef';
var emptyEntryPointParams = {};
var emptyEntryPointProps = {};
export function PopupTrigger(_ref) {
  var trigger = _ref.trigger,
    entryPoint = _ref.entryPoint,
    entryPointParams = _ref.entryPointParams,
    entryPointProps = _ref.entryPointProps,
    fallback = _ref.fallback,
    errorFallback = _ref.errorFallback,
    onError = _ref.onError,
    onClose = _ref.onClose,
    popupProps = _objectWithoutProperties(_ref, _excluded);
  var environmentProvider = useRelayEnvironmentProvider();
  var id = useMemo(function () {
    return entryPoint.root.getModuleName() || 'unknown';
  }, [entryPoint]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    content = _useState4[0],
    setContent = _useState4[1];
  var handleOpen = useCallback(function () {
    setIsOpen(true);
  }, []);
  var handleClose = useCallback(function (event, currentLevel) {
    setIsOpen(false);
    onClose === null || onClose === void 0 || onClose(event, currentLevel);
  }, [onClose]);
  var triggerCloseRef = useClickToCloseRef({
    isOpen: isOpen,
    handleClose: handleClose
  });
  var load = useCallback(function () {
    return loadEntryPoint(environmentProvider, entryPoint, entryPointParams !== null && entryPointParams !== void 0 ? entryPointParams : emptyEntryPointParams);
  }, [entryPoint, entryPointParams, environmentProvider]);
  var onLoad = useCallback(function (_ref2) {
    var entryPointReference = _ref2.reference;
    // avoid re-loading entry-point when closing popup
    if (isOpen) {
      return;
    }
    handleOpen();
    setContent( /*#__PURE__*/React.createElement(InternalEntryPointContainer, {
      id: id,
      entryPointReference: entryPointReference,
      fallback: fallback || /*#__PURE__*/React.createElement(Spinner, null),
      errorFallback: errorFallback,
      runtimeProps: _objectSpread(_objectSpread({}, entryPointProps !== null && entryPointProps !== void 0 ? entryPointProps : emptyEntryPointProps), {}, {
        onClose: handleClose
      }),
      onError: onError
    }));
  }, [setContent, id, isOpen, entryPointProps, fallback, errorFallback, handleOpen, onError, handleClose]);
  var triggerRef = usePressablePreloadRef({
    load: load,
    onLoad: onLoad
  });
  var popupTrigger = useCallback(function (triggerProps) {
    var mergedRef = mergeRefs([triggerRef, triggerProps.ref, triggerCloseRef]);
    return trigger(_objectSpread(_objectSpread({}, triggerProps), {}, {
      ref: mergedRef
    }));
  }, [trigger, triggerRef, triggerCloseRef]);
  var popupContent = useCallback(function () {
    return content;
  }, [content]);
  return /*#__PURE__*/React.createElement(Popup, _extends({}, popupProps, {
    isOpen: isOpen,
    onClose: handleClose,
    trigger: popupTrigger,
    content: popupContent
  }));
}